<template>
    <div class="recollection-status text-start" :class="`recollection-status--${tone}`">
        <h6 class="fw-semibold mb-1">{{ title }}</h6>
        <p v-if="refund" class="mb-0">
            <template v-if="request.status === 'requested'">
                You asked for a refund on {{ requestedOn }}. Our team will review your request.
            </template>
            <template v-else-if="request.status === 'approved'">
                We've approved your refund. We'll pay back what was paid for the kit, {{ deduction }}, to
                the card or account it was paid with.
            </template>
            <template v-else>
                We couldn't approve your refund. Please contact our support team at
                <a class="fw-semibold" href="mailto:help@livingdna.com">help@livingdna.com</a> and we'll help.
            </template>
        </p>
        <p v-else class="mb-0">
            <template v-if="request.status === 'requested'">
                You asked for a new sample kit on {{ requestedOn }}. Our team will review your request and
                email you when your new kit is on its way.
            </template>
            <template v-else-if="request.status === 'approved'">
                We've approved your request and are sending you a new sample kit. We'll email you when it's
                on its way.
            </template>
            <template v-else-if="request.status === 'replaced'">
                We've approved your request and your new sample kit is being posted to you. When it arrives,
                activate it, then follow the instructions inside to take a new sample and send it back to us.
            </template>
            <template v-else>
                We couldn't approve your request for a new sample kit. Please contact our support team at
                <a class="fw-semibold" href="mailto:help@livingdna.com">help@livingdna.com</a> and we'll help.
            </template>
        </p>
        <p v-if="trackingNumber" class="mb-0 mt-2">
            Tracking number: <span class="recollection-status__tracking fw-semibold">{{ trackingNumber }}</span>
        </p>
    </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { dateFormat } from "@/services/my-tests";

const props = defineProps({
    // { status, choice, requestedAt, trackingNumber, refundDeductionPercent } from the api; the
    // tracking number is the new kit's, once it has been posted
    request: { type: Object, required: true },
});

const titles = {
    new_kit: {
        requested: "Request received",
        approved: "Request approved",
        replaced: "Your new sample kit is on its way",
    },
    refund: {
        requested: "Refund requested",
        approved: "Refund approved",
    },
};

// Requests from before the refund (and the API before it) have no choice: they were all new kits.
const refund = computed(() => props.request.choice === "refund");
const title = computed(() => titles[refund.value ? "refund" : "new_kit"][props.request.status] ?? "We couldn't approve your request");
const tone = computed(() => (props.request.status === "rejected" ? "warning" : "info"));
const requestedOn = computed(() => dateFormat(props.request.requestedAt));
const deduction = computed(() =>
    props.request.refundDeductionPercent
        ? `less ${props.request.refundDeductionPercent}% for handling and shipping`
        : "less handling and shipping"
);
const trackingNumber = computed(() => (props.request.status === "replaced" ? props.request.trackingNumber : null));
</script>

<style lang="scss" scoped>
.recollection-status {
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    border-left: 4px solid;

    &--info {
        background: var(--bs-primary-bg-subtle, #e7f1ff);
        border-color: var(--bs-primary, #0d6efd);
    }

    &--warning {
        background: var(--bs-warning-bg-subtle, #fff3cd);
        border-color: var(--bs-warning, #ffc107);
    }

    // Tracking numbers run to 22 characters with no spaces; let them wrap on a phone.
    &__tracking {
        overflow-wrap: anywhere;
    }
}
</style>
